import { FC, useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CircularProgress,
  Alert,
  Button,
  Tabs,
  Tab,
  Chip,
  IconButton,
} from '@mui/material';
import {
  IconCalendar,
  IconCalendarEvent,
  IconEye,
  IconEyeOff,
  IconShip,
  IconMapPin,
  IconTrash,
  IconChevronUp,
  IconChevronDown,
  IconArrowsSort,
} from '@tabler/icons-react';
import useGetPartnerTrips from '../../../hooks/useGetPartnerTrips';
import { getErrorMessage } from '../../../utils/errorUtils';
import { format, parseISO } from 'date-fns';
import TripFilters from './components/TripFilters';
import { Dayjs } from 'dayjs';
import { getTripStatus } from '../../../constants/tripStatus';
import TripDeleteDialog from '../../../components/Partner/TripDeleteDialog';
import useGetPartnerBookings from '../../../hooks/useGetPartnerBookings';

type TripTab = 'all' | 'upcoming' | 'past';
type ViewMode = 'grid' | 'table' | 'calendar';
type SortColumn =
  | 'tripId'
  | 'boat'
  | 'route'
  | 'embarkation'
  | 'disembarkation'
  | 'dives'
  | 'status';
type SortDirection = 'asc' | 'desc';

const TripsList: FC = () => {
  const navigate = useNavigate();
  const {
    trips,
    isLoading,
    isError,
    error: errorDetails,
  } = useGetPartnerTrips();
  const { bookings } = useGetPartnerBookings();

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [boatFilter, setBoatFilter] = useState<number | 'all'>('all');
  const [routeFilter, setRouteFilter] = useState<number | 'all'>('all');
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);

  // Tab and view states
  const [activeTab, setActiveTab] = useState<TripTab>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [showPastTrips, setShowPastTrips] = useState<boolean>(true);

  // Sort states - always sort by embarkation date (descending - latest first)
  const [sortColumn, setSortColumn] = useState<SortColumn>('embarkation');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState<(typeof trips)[0] | null>(
    null,
  );

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Filter trips
  const filteredTrips = useMemo(() => {
    let filtered = [...trips];

    // Tab filter (upcoming vs past)
    const now = new Date();
    if (activeTab === 'upcoming') {
      filtered = filtered.filter(trip => new Date(trip.embarkationDate) >= now);
    } else if (activeTab === 'past') {
      filtered = filtered.filter(trip => new Date(trip.embarkationDate) < now);
    } else if (activeTab === 'all' && !showPastTrips) {
      // When on 'all' tab and showPastTrips is false, hide past trips
      filtered = filtered.filter(trip => new Date(trip.embarkationDate) >= now);
    }

    // Search filter
    if (debouncedSearchQuery.trim()) {
      const query = debouncedSearchQuery.toLowerCase();
      filtered = filtered.filter(
        trip =>
          trip.tripId.toString().includes(query) ||
          trip.boatName?.toLowerCase().includes(query) ||
          trip.routeName?.toLowerCase().includes(query) ||
          false,
      );
    }

    // Boat filter
    if (boatFilter !== 'all') {
      filtered = filtered.filter(trip => trip.boatId === boatFilter);
    }

    // Route filter
    if (routeFilter !== 'all') {
      filtered = filtered.filter(trip => trip.routeId === routeFilter);
    }

    // Date range filter
    if (startDate) {
      const start = startDate.toDate();
      filtered = filtered.filter(trip => {
        const embarkDate = parseISO(trip.embarkationDate);
        return embarkDate >= start;
      });
    }

    if (endDate) {
      const end = endDate.toDate();
      filtered = filtered.filter(trip => {
        const embarkDate = parseISO(trip.embarkationDate);
        return embarkDate <= end;
      });
    }

    return filtered;
  }, [
    trips,
    activeTab,
    showPastTrips,
    debouncedSearchQuery,
    boatFilter,
    routeFilter,
    startDate,
    endDate,
  ]);

  // Calculate tab counts (memoized for performance)
  const tabCounts = useMemo(() => {
    const now = new Date();
    return {
      all: trips.length,
      upcoming: trips.filter(t => new Date(t.embarkationDate) >= now).length,
      past: trips.filter(t => new Date(t.embarkationDate) < now).length,
    };
  }, [trips]);

  // Sort trips based on selected column and direction
  const sortedTrips = useMemo(() => {
    const sorted = [...filteredTrips];
    sorted.sort((a, b) => {
      let comparison = 0;

      switch (sortColumn) {
        case 'tripId':
          comparison = a.tripId - b.tripId;
          break;
        case 'boat': {
          const boatA = a.boatName || '';
          const boatB = b.boatName || '';
          comparison = boatA.localeCompare(boatB);
          break;
        }
        case 'route': {
          const routeA = a.routeName || '';
          const routeB = b.routeName || '';
          comparison = routeA.localeCompare(routeB);
          break;
        }
        case 'embarkation':
          comparison =
            new Date(a.embarkationDate).getTime() -
            new Date(b.embarkationDate).getTime();
          break;
        case 'disembarkation':
          comparison =
            new Date(a.disembarkationDate).getTime() -
            new Date(b.disembarkationDate).getTime();
          break;
        case 'dives': {
          const divesA = a.diveCount ?? 0;
          const divesB = b.diveCount ?? 0;
          comparison = divesA - divesB;
          break;
        }
        case 'status': {
          const statusA = getTripStatus({
            embarkationDate: a.embarkationDate,
            disembarkationDate: a.disembarkationDate,
          });
          const statusB = getTripStatus({
            embarkationDate: b.embarkationDate,
            disembarkationDate: b.disembarkationDate,
          });
          comparison = statusA.label.localeCompare(statusB.label);
          break;
        }
        default:
          comparison = 0;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
    return sorted;
  }, [filteredTrips, sortColumn, sortDirection]);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ column }: { column: SortColumn }) => {
    if (sortColumn !== column) {
      return <IconArrowsSort className="ml-1 h-4 w-4 text-gray-400" />;
    }
    return sortDirection === 'asc' ? (
      <IconChevronUp className="ml-1 h-4 w-4 text-gray-700" />
    ) : (
      <IconChevronDown className="ml-1 h-4 w-4 text-gray-700" />
    );
  };

  const handleClearAllFilters = () => {
    setSearchQuery('');
    setBoatFilter('all');
    setRouteFilter('all');
    setStartDate(null);
    setEndDate(null);
  };

  const handleDeleteClick = (trip: (typeof trips)[0], e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedTrip(trip);
    setDeleteDialogOpen(true);
  };

  const handleDeleteSuccess = () => {
    // The query will be invalidated automatically by the hook
    setDeleteDialogOpen(false);
    setSelectedTrip(null);
  };

  const getBookingCountForTrip = (tripId: number) => {
    return bookings.filter(b => b.tripId === tripId).length;
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <CircularProgress />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="mx-auto max-w-7xl">
        <Alert severity="error" className="mb-4">
          <div className="font-semibold">Error loading trips</div>
          <div className="mt-2 text-sm">{getErrorMessage(errorDetails)}</div>
        </Alert>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Trips</h1>
        <p className="mt-1 text-sm text-gray-500">
          View and manage all your trips
        </p>
      </div>

      {/* Filters */}
      <TripFilters
        trips={trips}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        boatFilter={boatFilter}
        onBoatChange={setBoatFilter}
        routeFilter={routeFilter}
        onRouteChange={setRouteFilter}
        startDate={startDate}
        onStartDateChange={setStartDate}
        endDate={endDate}
        onEndDateChange={setEndDate}
        onClearAll={handleClearAllFilters}
      />

      {/* Tabs and View Mode Toggle */}
      <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            className="min-w-0 flex-1"
          >
            <Tab label={`All (${tabCounts.all})`} value="all" />
            <Tab label={`Upcoming (${tabCounts.upcoming})`} value="upcoming" />
            <Tab label={`Past (${tabCounts.past})`} value="past" />
          </Tabs>

          {activeTab === 'all' && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowPastTrips(!showPastTrips)}
              startIcon={
                showPastTrips ? (
                  <IconEyeOff className="h-4 w-4" />
                ) : (
                  <IconEye className="h-4 w-4" />
                )
              }
              title={showPastTrips ? 'Hide past trips' : 'Show past trips'}
            >
              {showPastTrips ? 'Hide Past' : 'Show Past'}
            </Button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === 'grid' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setViewMode('grid')}
            startIcon={<IconShip className="h-4 w-4" />}
          >
            Grid
          </Button>
          <Button
            variant={viewMode === 'table' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setViewMode('table')}
            startIcon={<IconCalendar className="h-4 w-4" />}
          >
            Table
          </Button>
          <Button
            variant={viewMode === 'calendar' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setViewMode('calendar')}
            startIcon={<IconCalendarEvent className="h-4 w-4" />}
            disabled
            title="Calendar view coming soon"
          >
            Calendar
          </Button>
        </div>
      </div>

      {/* Results Count */}
      <div className="mb-4 text-sm text-gray-600">
        Showing {sortedTrips.length} of {trips.length} trip
        {trips.length !== 1 ? 's' : ''}
        {sortedTrips.length !== trips.length &&
          ` (filtered from ${trips.length} total)`}
      </div>

      {sortedTrips.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center">
          <IconShip className="mx-auto h-16 w-16 text-gray-400" />
          {trips.length === 0 ? (
            <>
              <p className="mt-4 text-gray-500">No trips found</p>
              <p className="mt-2 text-sm text-gray-400">
                Trips will appear here once they are created
              </p>
            </>
          ) : (
            <>
              <p className="mt-4 text-gray-500">No trips match your filters</p>
              <p className="mt-2 text-sm text-gray-400">
                Try adjusting your search criteria
              </p>
            </>
          )}
        </div>
      ) : viewMode === 'table' ? (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('tripId')}
                >
                  <div className="flex items-center">
                    Trip ID
                    <SortIcon column="tripId" />
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('boat')}
                >
                  <div className="flex items-center">
                    Boat
                    <SortIcon column="boat" />
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('route')}
                >
                  <div className="flex items-center">
                    Route
                    <SortIcon column="route" />
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('embarkation')}
                >
                  <div className="flex items-center">
                    Embarkation
                    <SortIcon column="embarkation" />
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('disembarkation')}
                >
                  <div className="flex items-center">
                    Disembarkation
                    <SortIcon column="disembarkation" />
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('dives')}
                >
                  <div className="flex items-center">
                    Dives
                    <SortIcon column="dives" />
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center">
                    Status
                    <SortIcon column="status" />
                  </div>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                  Actions
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                  Delete
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {sortedTrips.map(trip => {
                const status = getTripStatus({
                  embarkationDate: trip.embarkationDate,
                  disembarkationDate: trip.disembarkationDate,
                });
                return (
                  <tr
                    key={trip.tripId}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => navigate(`/partner/trips/${trip.tripId}`)}
                  >
                    <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                      {trip.tripId}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      {trip.boatName || 'N/A'}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      {trip.routeName || 'N/A'}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      {format(new Date(trip.embarkationDate), 'MMM dd, yyyy')}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      {format(
                        new Date(trip.disembarkationDate),
                        'MMM dd, yyyy',
                      )}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                      {trip.diveCount || 'N/A'}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm">
                      <Chip
                        label={status.label}
                        color={status.color}
                        size="small"
                      />
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          navigate(`/partner/trips/${trip.tripId}`);
                        }}
                        className="flex items-center gap-1 text-blue-600 hover:text-blue-900"
                      >
                        <IconEye className="h-4 w-4" />
                        View
                      </button>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-right text-sm">
                      <IconButton
                        size="small"
                        onClick={e => handleDeleteClick(trip, e)}
                        className="text-red-600 hover:text-red-800"
                        title="Delete trip"
                      >
                        <IconTrash className="h-4 w-4" />
                      </IconButton>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {sortedTrips.map(trip => {
            const status = getTripStatus({
              embarkationDate: trip.embarkationDate,
              disembarkationDate: trip.disembarkationDate,
            });
            return (
              <div
                key={trip.tripId}
                className="cursor-pointer rounded-lg border border-gray-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md"
                onClick={() => navigate(`/partner/trips/${trip.tripId}`)}
              >
                <div className="mb-3 flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {trip.boatName || 'Unnamed Boat'}
                    </h3>
                    <div className="mt-1 flex items-center gap-2 text-sm text-gray-500">
                      <IconMapPin className="h-4 w-4" />
                      <span>{trip.routeName || 'N/A'}</span>
                    </div>
                  </div>
                  <Chip
                    label={status.label}
                    color={status.color}
                    size="small"
                  />
                </div>
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <IconCalendar className="h-4 w-4 text-gray-400" />
                    <span>
                      {format(new Date(trip.embarkationDate), 'MMM dd, yyyy')} -{' '}
                      {format(
                        new Date(trip.disembarkationDate),
                        'MMM dd, yyyy',
                      )}
                    </span>
                  </div>
                  {trip.diveCount && (
                    <div className="flex items-center gap-2">
                      <IconShip className="h-4 w-4 text-gray-400" />
                      <span>{trip.diveCount} dives</span>
                    </div>
                  )}
                </div>
                <div className="mt-4 flex justify-between">
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      handleDeleteClick(trip, e);
                    }}
                    className="flex items-center gap-1 text-sm text-red-600 hover:text-red-900"
                    title="Delete trip"
                  >
                    <IconTrash className="h-4 w-4" />
                    Delete
                  </button>
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      navigate(`/partner/trips/${trip.tripId}`);
                    }}
                    className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-900"
                  >
                    <IconEye className="h-4 w-4" />
                    View Details
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Delete Dialog */}
      <TripDeleteDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedTrip(null);
        }}
        trip={selectedTrip}
        onSuccess={handleDeleteSuccess}
        bookingCount={
          selectedTrip ? getBookingCountForTrip(selectedTrip.tripId) : 0
        }
      />
    </div>
  );
};

export default TripsList;
